import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, Button, Select, Modal, Input, Badge } from '@/components/ui';
import {
  Plus,
  ChevronLeft,
  ChevronRight,
  Trash2,
} from 'lucide-react';
import { format, addDays, startOfWeek, isSameDay, parseISO } from 'date-fns';
import { clsx } from 'clsx';

interface Showtime {
  id: string;
  movieId: string;
  movie: { id: string; title: string; rating?: string; runtime?: number };
  screenId: string;
  screen: { id: string; name: string };
  startsAt: string;
  format: string;
  priceType: string;
  isActive: boolean;
}

interface Movie {
  id: string;
  title: string;
  rating?: string;
  runtime?: number;
}

interface Screen {
  id: string;
  name: string;
}

const formatOptions = [
  { value: '2D', label: '2D' },
  { value: '3D', label: '3D' },
];

const priceTypeOptions = [
  { value: 'matinee', label: 'Matinee' },
  { value: 'evening', label: 'Evening' },
];

export function ShowtimesPage() {
  const queryClient = useQueryClient();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingShowtime, setEditingShowtime] = useState<Showtime | null>(null);

  const weekStart = startOfWeek(selectedDate, { weekStartsOn: 0 });
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  // Fetch showtimes for the week
  const { data: showtimes, isLoading } = useQuery<Showtime[]>({
    queryKey: ['showtimes', format(weekStart, 'yyyy-MM-dd')],
    queryFn: async () => {
      const dateFrom = format(weekStart, 'yyyy-MM-dd');
      const dateTo = format(addDays(weekStart, 6), 'yyyy-MM-dd');
      const response = await api.get(`/admin/showtimes?dateFrom=${dateFrom}&dateTo=${dateTo}`);
      return response.data?.data?.items || [];
    },
  });

  // Fetch movies and screens for the form
  const { data: movies } = useQuery<Movie[]>({
    queryKey: ['movies-active'],
    queryFn: async () => {
      const response = await api.get('/admin/movies?status=published');
      return response.data?.data?.items || response.data?.data || [];
    },
  });

  const { data: screens } = useQuery<Screen[]>({
    queryKey: ['screens'],
    queryFn: async () => {
      const response = await api.get('/admin/screens');
      return response.data?.data?.items || response.data?.data || [];
    },
  });

  // Group showtimes by date
  const showtimesByDate = useMemo(() => {
    const grouped: Record<string, Showtime[]> = {};
    showtimes?.forEach((showtime) => {
      const dateKey = format(parseISO(showtime.startsAt), 'yyyy-MM-dd');
      if (!grouped[dateKey]) grouped[dateKey] = [];
      grouped[dateKey].push(showtime);
    });
    // Sort each day's showtimes by time
    Object.keys(grouped).forEach((key) => {
      grouped[key].sort((a, b) =>
        new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime()
      );
    });
    return grouped;
  }, [showtimes]);

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/admin/showtimes/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['showtimes'] });
    },
  });

  function handlePrevWeek() {
    setSelectedDate((prev) => addDays(prev, -7));
  }

  function handleNextWeek() {
    setSelectedDate((prev) => addDays(prev, 7));
  }

  function handleToday() {
    setSelectedDate(new Date());
  }

  function openCreateModal(date?: Date) {
    setEditingShowtime(null);
    setSelectedDate(date ?? new Date());
    setIsModalOpen(true);
  }

  function openEditModal(showtime: Showtime) {
    setEditingShowtime(showtime);
    setIsModalOpen(true);
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Showtimes</h1>
          <p className="text-gray-400 mt-1">Manage your movie schedule</p>
        </div>
        <Button onClick={() => openCreateModal()}>
          <Plus className="h-4 w-4 mr-2" />
          Add Showtime
        </Button>
      </div>

      {/* Calendar Navigation */}
      <Card padding="md">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handlePrevWeek}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={handleNextWeek}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleToday}>
              Today
            </Button>
          </div>
          <h2 className="text-lg font-semibold text-white">
            {format(weekStart, 'MMMM d')} - {format(addDays(weekStart, 6), 'MMMM d, yyyy')}
          </h2>
          <div className="w-32" /> {/* Spacer for alignment */}
        </div>
      </Card>

      {/* Week View */}
      <div className="grid grid-cols-7 gap-4">
        {weekDays.map((day) => {
          const dateKey = format(day, 'yyyy-MM-dd');
          const dayShowtimes = showtimesByDate[dateKey] ?? [];
          const isToday = isSameDay(day, new Date());

          return (
            <Card
              key={dateKey}
              padding="none"
              className={clsx(isToday && 'ring-2 ring-gold')}
            >
              {/* Day Header */}
              <div
                className={clsx(
                  'px-3 py-2 border-b border-midnight-100 text-center',
                  isToday ? 'bg-gold/10' : 'bg-midnight-100/50'
                )}
              >
                <p className="text-xs text-gray-400 uppercase">
                  {format(day, 'EEE')}
                </p>
                <p className={clsx('text-lg font-semibold', isToday ? 'text-gold' : 'text-white')}>
                  {format(day, 'd')}
                </p>
              </div>

              {/* Showtimes */}
              <div className="p-2 min-h-[200px] space-y-2">
                {isLoading ? (
                  <div className="flex items-center justify-center h-20">
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-gold border-t-transparent" />
                  </div>
                ) : dayShowtimes.length === 0 ? (
                  <button
                    onClick={() => openCreateModal(day)}
                    className="w-full h-20 flex flex-col items-center justify-center text-gray-500 hover:text-gray-400 hover:bg-midnight-100/50 rounded-lg transition-colors"
                  >
                    <Plus className="h-5 w-5 mb-1" />
                    <span className="text-xs">Add</span>
                  </button>
                ) : (
                  <>
                    {dayShowtimes.map((showtime) => (
                      <button
                        key={showtime.id}
                        onClick={() => openEditModal(showtime)}
                        className="w-full p-2 rounded-lg bg-midnight-100 hover:bg-midnight-100/80 text-left transition-colors group"
                      >
                        <div className="flex items-start justify-between">
                          <span className="text-xs font-medium text-gold">
                            {format(parseISO(showtime.startsAt), 'h:mm a')}
                          </span>
                          {showtime.format === '3D' && (
                            <Badge variant="info" className="text-[10px]">3D</Badge>
                          )}
                        </div>
                        <p className="text-sm text-white truncate mt-1">
                          {showtime.movie.title}
                        </p>
                        <p className="text-xs text-gray-500">{showtime.screen.name}</p>
                      </button>
                    ))}
                    <button
                      onClick={() => openCreateModal(day)}
                      className="w-full p-2 flex items-center justify-center text-gray-500 hover:text-gray-400 hover:bg-midnight-100/50 rounded-lg transition-colors"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Create/Edit Modal */}
      <ShowtimeModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingShowtime(null);
        }}
        showtime={editingShowtime}
        selectedDate={selectedDate}
        movies={movies ?? []}
        screens={screens ?? []}
        onDelete={(id) => {
          if (confirm('Delete this showtime?')) {
            deleteMutation.mutate(id);
            setIsModalOpen(false);
          }
        }}
      />
    </div>
  );
}

// Showtime Modal Component
interface ShowtimeModalProps {
  isOpen: boolean;
  onClose: () => void;
  showtime: Showtime | null;
  selectedDate: Date;
  movies: Movie[];
  screens: Screen[];
  onDelete: (id: string) => void;
}

function ShowtimeModal({
  isOpen,
  onClose,
  showtime,
  selectedDate,
  movies,
  screens,
  onDelete,
}: ShowtimeModalProps) {
  const queryClient = useQueryClient();
  const isEdit = !!showtime;

  const [formData, setFormData] = useState({
    movieId: '',
    screenId: '',
    date: format(selectedDate, 'yyyy-MM-dd'),
    time: '19:00',
    format: '2D',
    priceType: 'evening',
  });

  // Update form when showtime changes (for edit mode)
  useEffect(() => {
    if (showtime) {
      const startsAt = parseISO(showtime.startsAt);
      setFormData({
        movieId: showtime.movieId,
        screenId: showtime.screenId,
        date: format(startsAt, 'yyyy-MM-dd'),
        time: format(startsAt, 'HH:mm'),
        format: showtime.format,
        priceType: showtime.priceType,
      });
    } else {
      setFormData({
        movieId: '',
        screenId: screens[0]?.id ?? '',
        date: format(selectedDate, 'yyyy-MM-dd'),
        time: '19:00',
        format: '2D',
        priceType: 'evening',
      });
    }
  }, [showtime, selectedDate, screens]);

  const saveMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const payload = {
        movieId: data.movieId,
        screenId: data.screenId,
        startsAt: `${data.date}T${data.time}:00`,
        format: data.format,
        priceType: data.priceType,
      };
      if (isEdit) {
        return api.put(`/admin/showtimes/${showtime.id}`, payload);
      }
      return api.post('/admin/showtimes', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['showtimes'] });
      onClose();
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    saveMutation.mutate(formData);
  }

  const movieOptions = [
    { value: '', label: 'Select movie' },
    ...movies.map((m) => ({ value: m.id, label: m.title })),
  ];

  const screenOptions = [
    { value: '', label: 'Select screen' },
    ...screens.map((s) => ({ value: s.id, label: s.name })),
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEdit ? 'Edit Showtime' : 'Add Showtime'}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Select
          label="Movie"
          options={movieOptions}
          value={formData.movieId}
          onChange={(e) => setFormData((prev) => ({ ...prev, movieId: e.target.value }))}
          required
        />

        <Select
          label="Screen"
          options={screenOptions}
          value={formData.screenId}
          onChange={(e) => setFormData((prev) => ({ ...prev, screenId: e.target.value }))}
          required
        />

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Date"
            type="date"
            value={formData.date}
            onChange={(e) => setFormData((prev) => ({ ...prev, date: e.target.value }))}
            required
          />
          <Input
            label="Time"
            type="time"
            value={formData.time}
            onChange={(e) => setFormData((prev) => ({ ...prev, time: e.target.value }))}
            required
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Format"
            options={formatOptions}
            value={formData.format}
            onChange={(e) => setFormData((prev) => ({ ...prev, format: e.target.value }))}
          />
          <Select
            label="Price Type"
            options={priceTypeOptions}
            value={formData.priceType}
            onChange={(e) => setFormData((prev) => ({ ...prev, priceType: e.target.value }))}
          />
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-midnight-100">
          {isEdit ? (
            <Button
              type="button"
              variant="ghost"
              onClick={() => onDelete(showtime.id)}
              className="text-red-400 hover:text-red-300"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          ) : (
            <div />
          )}
          <div className="flex items-center gap-3">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={saveMutation.isPending}>
              {isEdit ? 'Update' : 'Create'}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
